import type { ContactPolicyContact } from "@shared/contactPolicy";
import { emailSend, smsSend } from "@shared/schemas/messages";
import { evaluation, parseInput } from "@shared/validation/issues";
import { validateMessage } from "@shared/validation/messages";

/** Presentation adapter only: the command schema and shared contact policy own
 * every rule. The server still resolves relationships and enforces delivery. */
export function manualMessageFindings(channel: "sms" | "email", input: Record<string, unknown>, contact: ContactPolicyContact | null) {
  const action = channel === "sms" ? "comms.send_sms" : "comms.send_email";
  const command = parseInput(channel === "sms" ? smsSend : emailSend, input, { type: "message" }, action);
  const record = validateMessage({ ...input, kind: "manual", channel, direction: "out" }, { action, contact });
  return evaluation([...command.issues, ...record.issues], false);
}

/** A queued/held message is accepted, not delivered. Never discard a draft for
 * a failed, cancelled or unrecognized response, and never retry automatically. */
export function manualSendFeedback(channel: "sms" | "email", status: string, error?: string | null) {
  const label = channel === "sms" ? "Text" : "Email";
  switch (status) {
    case "held": return { accepted: true, message: `${label} held for sending` };
    case "queued": return { accepted: true, message: `${label} queued` };
    case "sending": return { accepted: true, message: `${label} sending` };
    case "sent": return { accepted: true, message: `${label} sent` };
    case "delivered": return { accepted: true, message: `${label} delivered` };
    // Provider adapters also use failed for timeouts: the provider may have
    // accepted the request before the response was lost. Do not infer delivery.
    case "failed": return { accepted: false, message: `${label} delivery was not confirmed: ${error || "provider error"}. Your draft is retained. Check the conversation before sending again.` };
    case "bounced": return { accepted: false, message: `${label} not delivered: ${error || "provider error"}. Your draft is retained.` };
    case "cancelled": return { accepted: false, message: `${label} cancelled. Your draft is retained.` };
    default: return { accepted: false, message: `${label} delivery could not be confirmed. Check the conversation before sending again. Your draft is retained.` };
  }
}

export const uncertainSendMessage = "Delivery could not be confirmed. Your draft is retained. Check the conversation before sending again.";
